import Badge from '@/components/shared/Badge/Badge'
import { FileIcon, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface DeliverableCardProps {
  deliverableName: string
  deliverableSize: number | string
  id: string
  url?: string
  onDelete: () => void
  meta?: string
  badgeStatus?: string
  badgeText?: string
  internalNote?: string
}

export default function DeliverableCard({
  onDelete,
  deliverableName,
  deliverableSize,
  url,
  meta,
  badgeStatus,
  badgeText,
  internalNote,
}: DeliverableCardProps) {
  const { t } = useTranslation('common')

  const handleOpenDocument = () => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }

  const sizeLabel =
    typeof deliverableSize === 'string' &&
    /[kmgt]b$/i.test(String(deliverableSize).trim())
      ? String(deliverableSize)
      : `${deliverableSize} MB`


  return (
    <div
      className={`mt-2 border relative border-borderColor min-h-[74px] rounded-md p-4 pr-10 ${
        url ? 'cursor-pointer hover:bg-muted/10' : ''
      }`}
      onClick={url ? handleOpenDocument : undefined}
      tabIndex={url ? 0 : -1}
      role={url ? 'link' : undefined}
      aria-label={url ? deliverableName : undefined}
    >
      <article className="flex w-full items-start gap-2">
        <FileIcon className="text-white w-4 h-4 shrink-0 mt-0.5" />
        <article className="flex-1 min-w-0 space-y-1.5">
          {url ? (
            <span className="text-sm block line-clamp-2 hover:underline">
              {deliverableName}
            </span>
          ) : (
            <p className="text-sm line-clamp-2">{deliverableName}</p>
          )}
          <p className="text-sm text-muted">{sizeLabel}</p>
          {meta ? (
            <p className="text-xs text-muted line-clamp-2">{meta}</p>
          ) : null}
          {badgeStatus ? <Badge status={badgeStatus} text={badgeText} /> : null}
          {internalNote ? (
            <p className="text-xs text-red-500 line-clamp-2">{internalNote}</p>
          ) : null}
        </article>
        <X
          className="text-white absolute right-4 top-4 w-4 h-4 cursor-pointer shrink-0"
          aria-label={t('delete_products')}
          onClick={(e) => {
            e.stopPropagation()
            onDelete()
          }}
          data-cy="deliverable-delete-button"
        />
      </article>
    </div>
  )
}
